'use client';

import React from 'react';

import { useLocale } from 'next-intl';

import Lines from '@/app/[locale]/(manual)/guide/[slug]/components/Lines';
import { MediaDetail } from '@/app/[locale]/(manual)/guide/[slug]/components/MediaDetail';
import Navigation from '@/app/[locale]/(manual)/guide/[slug]/components/Navigation';
import { useHashNavigation } from '@/app/[locale]/(manual)/guide/[slug]/hooks/useHashNavigation';
import { useImagePreloader } from '@/app/[locale]/(manual)/guide/[slug]/hooks/useImagePreloader';
import { useKeyboardNavigation } from '@/app/[locale]/(manual)/guide/[slug]/hooks/useKeyboardNavigation';
import { useLanguageOptions } from '@/app/[locale]/(manual)/guide/[slug]/hooks/useLanguageOptions';
import { useMediaUrlUpdate } from '@/app/[locale]/(manual)/guide/[slug]/hooks/useMediaUrlUpdate';
import { useNavigation } from '@/app/[locale]/(manual)/guide/[slug]/hooks/useNavigation';
import GuideCategory from '@/app/[locale]/(manual)/manual/[slug]/GuideCategory';
import EditInAdminButton from '@/ui/components/EditInAdminButton';
import { useGlobalState } from '@/ui/components/GlobalStateProvider';
import Loader from '@/ui/components/Loader';
import {
  ManualLoading,
  ManualMainMain,
  ManualMain,
  ManualContent,
  ManualPhoto,
  ManualPhotoControls,
  ManualPhotoSlider,
  ManualPhotoSliderImg,
} from '@/ui/components/Manual/manual.styled';
import { useManual } from '@/ui/components/Manual/ManualContextProvider';
import { useHashNavigation as useReplaceHash } from '@/utils/hashChange/hooks';
import { getYoutubeId } from '@/utils/helpers';

interface ManualProps {
  category: CategoryData;
  guides: GuideData[];
  pdfs: PdfData[];
}

export default function Manual({ category, guides, pdfs }: ManualProps) {
  const locale = useLocale();
  const [isHovered, setIsHovered] = React.useState(false);

  const { activeStep, activePhoto, setActivePhoto, setActiveStep, sidebar, steps, guide, siblings } = useManual();
  const { setLanguageOptions } = useGlobalState();
  const { replaceWithHash } = useReplaceHash();

  // Use the navigation hook to handle all navigation logic
  const {
    hasPrevSlideOrStep,
    hasNextSlideOrStep,
    currentStep,
    currentStepGallery,
    currentStepVideo,
    prevSlide,
    nextSlide,
    prevStep,
    nextStep,
    isLoading,
    isNavigating,
    canNavigate,
  } = useNavigation({
    locale,
    activeStep,
    activePhoto,
    setActiveStep,
    setActivePhoto,
    steps,
    guide,
    siblings,
  });

  // Use custom hooks for effects
  const currentHash = useHashNavigation({ 
    steps, 
    activeStep, 
    setActiveStep, 
    setActivePhoto, 
    replaceWithHash, 
    isNavigating 
  });
  
  useLanguageOptions({ guide, currentHash, steps, setLanguageOptions });

  // Use custom hooks for various effects
  useMediaUrlUpdate(steps);
  useImagePreloader({ steps, activeStep, setActivePhoto });
  useKeyboardNavigation({
    activeStep,
    activePhoto,
    hasNextSlideOrStep,
    prevStep,
    nextStep,
    prevSlide,
    nextSlide,
  });

  if (!currentStepGallery && !currentStepVideo) return null;

  if (!steps[activeStep]) return null;

  return (
    <ManualMainMain>
      {isLoading && (
        <ManualLoading>
          <Loader />
        </ManualLoading>
      )}
      <ManualMain $isLoading={isLoading}>
        {/* Entry step for non-assembly manuals */}
        {steps?.[activeStep]?.entryStep ? (
          <GuideCategory
            category={category}
            entryStep={true}
            nextStep={steps?.[1]?.id ?? null}
            guides={guides}
            pdfs={pdfs}
          />
        ) : (
          <>
            <ManualContent onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
              <EditInAdminButton item={currentStep} visible={isHovered} parentSlug={guide?.slug} />
              <Lines data={currentStep.lines} />
            </ManualContent>
            <ManualPhoto $isTabOpen={sidebar !== null}>
              {currentStepGallery ? (
                <MediaDetail
                  imageData={currentStep.media.gallery[activePhoto]?.child || currentStep.media.gallery[activePhoto]}
                  //src={currentStep.media.gallery[activePhoto]?.medium_large || null}
                  alt={currentStep?.title || ''}
                  isTabOpen={sidebar !== null}
                />
              ) : (
                currentStepVideo && (
                  <MediaDetail
                    youtubeSrc={currentStepVideo}
                    alt={currentStep?.title || ''}
                    isTabOpen={sidebar !== null}
                  />
                )
              )}
              <ManualPhotoControls>
                <ManualPhotoSlider>
                  {currentStepGallery
                    ? currentStepGallery.map((img, i) => (
                        <ManualPhotoSliderImg
                          key={img.id}
                          src={img.child?.thumbnail || img.thumbnail}
                          onClick={() => setActivePhoto(i)}
                          $isActive={activePhoto === i}
                        />
                      ))
                    : currentStepVideo && (
                        <ManualPhotoSliderImg
                          src={`https://img.youtube.com/vi/${getYoutubeId(currentStepVideo)}/default.jpg`}
                          $isActive={true}
                        />
                      )}
                </ManualPhotoSlider>

                <Navigation
                  isTabOpen={sidebar !== null}
                  activePhoto={activePhoto}
                  hasPrevSlideOrStep={hasPrevSlideOrStep}
                  hasNextSlideOrStep={hasNextSlideOrStep}
                  currentStepGallery={currentStepGallery}
                  currentStepVideo={currentStepVideo}
                  prevSlide={prevSlide}
                  nextSlide={nextSlide}
                  prevStep={prevStep}
                  nextStep={nextStep}
                  isLoading={isLoading}
                  canNavigate={canNavigate()}
                />
              </ManualPhotoControls>
            </ManualPhoto>
          </>
        )}
      </ManualMain>
    </ManualMainMain>
  );
}
